import { useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';
import { User } from '../types/store';

/**
 * Custom hook for authentication state and actions
 * Provides a clean API for components to interact with auth state
 */
export const useAuth = () => {
  const store = useAuthStore();
  
  // Stabilize function references to prevent infinite loops in useEffect
  const signIn = useCallback(
    (user: User) => store.signIn(user),
    [store.signIn]
  );
  
  const signOut = useCallback(
    () => store.signOut(),
    [store.signOut]
  );
  
  const setFirstTimeSignin = useCallback(
    (value: boolean) => store.setFirstTimeSignin(value),
    [store.setFirstTimeSignin]
  );
  
  const clearFirstTimeSignin = useCallback(
    () => store.clearFirstTimeSignin(),
    [store.clearFirstTimeSignin]
  );
  
  const enableFirstTimeSignin = useCallback(
    () => store.enableFirstTimeSignin(),
    [store.enableFirstTimeSignin]
  );
  
  const checkAuthStatus = useCallback(
    () => store.checkAuthStatus(),
    [store.checkAuthStatus]
  );
  
  const initialize = useCallback(
    () => store.initialize(),
    [store.initialize]
  );

  const completeOnboarding = useCallback(
    () => store.completeOnboarding(),
    [store.completeOnboarding]
  );
  
  return {
    // State
    isAuthenticated: store.isAuthenticated,
    isLoading: store.isLoading,
    user: store.user,
    firstTimeSignin: store.firstTimeSignin,
    
    // Computed values
    isSignedIn: store.isAuthenticated && !!store.user,
    isSignedOut: !store.isAuthenticated && !store.isLoading,
    
    // Actions (stabilized)
    signIn,
    signOut,
    setFirstTimeSignin,
    clearFirstTimeSignin,
    enableFirstTimeSignin,
    checkAuthStatus,
    initialize,
    completeOnboarding,
  };
};
